import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { ResourceType } from '@cotera/api';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import { ChatArtifactModel } from '@cotera/client/v0/chat/chat-artifact.model';
import { resourceArtifactData } from '@cotera/client/v0/chat/artifacts';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

const addResourceArtifactInputSchema = z.object({
  resourceType: z.nativeEnum(ResourceType),
  resourceId: z.string().min(1),
});

type Params = z.infer<typeof addResourceArtifactInputSchema>;

/**
 * Adds a workspace resource (agent / document / folder / …) to the chat's artifact
 * panel and focuses it. Generalizes the former agent-only action so clicking any
 * artifact-viewable resource mention opens it in the panel.
 */
export class AddResourceArtifactAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Open in Chat';
  inputSchema = addResourceArtifactInputSchema;
  group = 'Chat';
  description = 'Open a resource in the chat artifact panel';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const result = await this.client.addAction({
      conversationId: model.conversationId,
      action: {
        type: 'add_artifact',
        artifactType: params.resourceType,
        artifactId: params.resourceId,
        data: resourceArtifactData(params.resourceType, params.resourceId),
      },
    });

    if (result.isErr()) {
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to add artifact';
      return err([{ t: 'error', message: errorMessage }]);
    }

    // Focus the panel on what we just opened (also handles the already-present case,
    // where auto-focus-newest wouldn't fire because no new artifact was added).
    context
      .getInScopeModelOfType(ChatArtifactModel)
      ?.requestFocus(params.resourceId);

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return {
      event: CocoEvent.ResourceArtifactAdded,
      properties: {
        conversationId: model.conversationId,
        resourceType: payload.resourceType,
        resourceId: payload.resourceId,
      },
    };
  }
}
